import React, { useState, useEffect } from 'react';
import { getBusinessProfileDefaults, getIndustryDefaults } from '../../utils/businessProfileDefaults';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    CircularProgress,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Switch,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Radio,
    RadioGroup,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Create as CreateIcon,
    Article as ArticleIcon,
    Image as ImageIcon,
    ExpandMore as ExpandMoreIcon,
    PlayArrow as ExecuteIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Web as WebIcon,
    Settings as SettingsIcon,
    Business,
    Warning,
    GetApp as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axiosWithAuth from '../../utils/axiosWithAuth';
import { isCASOnlySubscription } from '../../utils/subscriptionUtils';

const ContentGenerator = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [businessProfile, setBusinessProfile] = useState(null);
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [subscriptionProfiles, setSubscriptionProfiles] = useState({});

    // Generation state
    const [generating, setGenerating] = useState(false);
    const [generationType, setGenerationType] = useState('website');
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

    // Form state with intelligent defaults
    const [generationForm, setGenerationForm] = useState(() => {
        const defaults = getBusinessProfileDefaults();
        return {
            // Basic fields - user must provide
            entity_name: '',
            business_description: '',
            services_products_description: '',
            target_audience: '',
            contact_info: {
                email: '',
                phone: '',
                address: ''
            },

            // Operation-specific fields
            requested_pages: '',
            requested_blog_topics: '',
            additional_blog_topics: '',
            requested_image_prompts: '',

            // Generation flags with defaults
            image_generation_required: defaults.image_generation_required,
            use_default_placeholder_images: defaults.use_default_placeholder_images,
            page_generation_mode: defaults.page_generation_mode,
            blog_generation_mode: defaults.blog_generation_mode,
            include_default_pages: true,
            generate_blogs: true,

            // Advanced fields with intelligent defaults
            key_value_propositions: defaults.key_value_propositions.join('\n'),
            brand_attributes: defaults.brand_attributes.join(', '),
            seo_primary_keywords: '',
            seo_secondary_keywords: '',
            text_voice: defaults.text_voice,
            text_formality: defaults.text_formality,
            image_art_style: defaults.image_art_style,
            image_composition_preference: defaults.image_composition_preference,
            image_dominant_colors: defaults.image_dominant_colors.join(', '),
            negative_image_prompts: defaults.negative_image_prompts.join('\n'),
            website_style_preference: defaults.website_style_preference,
            primary_call_to_action: defaults.primary_call_to_action,
            secondary_call_to_actions: defaults.secondary_call_to_actions.join('\n'),
            content_to_exclude: defaults.content_to_exclude.join('\n'),
            // Additional fields
            default_banner_placeholder_url: '',
            default_content_placeholder_url: '',
            homepage_section_order_preference: defaults.homepage_section_order_preference.join(', '),
            logo_url: '',
            social_media_links: ''
        };
    });

    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);

    useEffect(() => {
        fetchUserData();
    }, []);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const subscriptionsRes = await axiosWithAuth.get('/subscriptions/my-subscriptions');
            const activeSubscriptions = subscriptionsRes.data.filter(sub =>
                sub.status === 'active' && !isCASOnlySubscription(sub)
            );
            setUserSubscriptions(activeSubscriptions);

            if (activeSubscriptions.length > 0) {
                setSelectedSubscription(activeSubscriptions[0]);
            }

            // Fetch business profiles for all subscriptions
            const profilePromises = activeSubscriptions.map(subscription =>
                axiosWithAuth.get(`/subscriptions/${subscription.id}/business-profile`)
                    .catch(error => {
                        if (error.response?.status === 404) {
                            return { data: null };
                        }
                        throw error;
                    })
            );

            const profileResponses = await Promise.all(profilePromises);
            const profilesMap = {};
            activeSubscriptions.forEach((subscription, index) => {
                profilesMap[subscription.id] = profileResponses[index].data;
            });
            setSubscriptionProfiles(profilesMap);

            // Set initial business profile if available
            if (activeSubscriptions.length > 0) {
                const firstProfile = profilesMap[activeSubscriptions[0].id];
                if (firstProfile) {
                    setBusinessProfile(firstProfile);
                    updateGenerationForm(firstProfile);
                }
            }
        } catch (error) {
            console.error('Failed to fetch user data:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateGenerationForm = (profile) => {
        setGenerationForm({
            entity_name: profile.entity_name || '',
            business_description: profile.business_description || '',
            services_products_description: profile.services_products_description || '',
            target_audience: profile.target_audience || '',
            contact_info: {
                email: profile.contact_email || '',
                phone: profile.contact_phone || '',
                address: profile.contact_address || ''
            },
            key_value_propositions: profile.key_value_propositions?.join(', ') || '',
            brand_attributes: profile.brand_attributes?.join(', ') || '',
            seo_primary_keywords: profile.seo_primary_keywords?.join(', ') || '',
            seo_secondary_keywords: profile.seo_secondary_keywords?.join(', ') || '',
            text_voice: profile.text_voice || 'Third Person (They/The Company)',
            text_formality: profile.text_formality || 'Formal',
            image_art_style: profile.image_art_style || 'photorealistic',
            image_composition_preference: profile.image_composition_preference || '',
            image_dominant_colors: profile.image_dominant_colors?.join(', ') || '',
            negative_image_prompts: profile.negative_image_prompts?.join(', ') || '',
            website_style_preference: profile.website_style_preference || '',
            primary_call_to_action: profile.primary_call_to_action || '',
            secondary_call_to_actions: profile.secondary_call_to_actions?.join(', ') || '',
            content_to_exclude: profile.content_to_exclude?.join(', ') || '',
            // New fields
            use_default_placeholder_images: profile.use_default_placeholder_images ?? false,
            default_banner_placeholder_url: profile.default_banner_placeholder_url || '',
            default_content_placeholder_url: profile.default_content_placeholder_url || '',
            homepage_section_order_preference: profile.homepage_section_order_preference?.join(', ') || '',
            logo_url: profile.logo_url || '',
            social_media_links: profile.social_media_links?.join(', ') || '',
            requested_pages: '',
            requested_blog_topics: '',
            requested_image_prompts: '',
            image_generation_required: true,
            page_generation_mode: 'standard',
            blog_generation_mode: 'standard',
            include_default_pages: true,
            generate_blogs: true
        });
    };

    const handleGenerateContent = async () => {
        try {
            setGenerating(true);
            setError('');
            setSuccess('');

            // Determine operation type based on generation type and flags
            let operationType;
            if (generationType === 'images') {
                operationType = 'generate_images';
            } else if (generationType === 'blogs') {
                operationType = 'generate_blogs';
            } else if (generationType === 'pages') {
                operationType = 'generate_pages';
            } else {
                operationType = 'create_website';
            }

            // Prepare parameters based on operation type
            const parameters = {
                entity_name: generationForm.entity_name,
                business_description: generationForm.business_description,
                services_products_description: generationForm.services_products_description,
                target_audience: generationForm.target_audience,
                contact_info: {
                    email: generationForm.contact_info.email,
                    phone: generationForm.contact_info.phone,
                    address: generationForm.contact_info.address
                },
                subscription_id: selectedSubscription?.id
            };

            // Set dump_content_files based on operation type per configuration matrix
            if (['generate_images', 'generate_blogs', 'generate_pages'].includes(operationType)) {
                parameters.dump_content_files = true;  // No WordPress site - content/images only
            } else {
                parameters.dump_content_files = false; // WordPress site creation
            }

            // Add business profile data if available
            if (businessProfile) {
                parameters.business_profile_id = businessProfile.id;
            }

            // Add operation-specific parameters
            if (operationType === 'generate_images') {
                // Add required flags for image-only generation
                parameters.page_generation_mode = 'custom';
                parameters.blog_generation_mode = 'custom';
                parameters.requested_pages = [];
                parameters.requested_blog_topics = [];
            }

            // Process image prompts for any operation that uses images (only if provided)
            if (generationForm.requested_image_prompts && generationForm.requested_image_prompts.trim() && ['generate_images', 'generate_pages', 'generate_blogs', 'create_website'].includes(operationType)) {
                // Parse image prompts in identifier:prompt format
                const imagePrompts = generationForm.requested_image_prompts
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line && line.includes(':'))
                    .map(line => {
                        const [identifier, ...promptParts] = line.split(':');
                        const prompt = promptParts.join(':').trim(); // Handle prompts with colons
                        return {
                            identifier: identifier.trim(),
                            prompt: prompt,
                            style: generationForm.image_art_style || 'photorealistic'
                        };
                    });

                if (imagePrompts.length > 0) {
                    parameters.requested_image_prompts = imagePrompts;
                } else {
                    // Fallback: if no valid identifier:prompt format, create default
                    const fallbackPrompts = generationForm.requested_image_prompts
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line)
                        .map((prompt, index) => ({
                            identifier: `image_${index + 1}`,
                            prompt: prompt,
                            style: generationForm.image_art_style || 'photorealistic'
                        }));
                    parameters.requested_image_prompts = fallbackPrompts;
                }

                parameters.image_art_style = generationForm.image_art_style;
                parameters.image_composition_preference = generationForm.image_composition_preference;
                parameters.image_dominant_colors = generationForm.image_dominant_colors.split(',').map(t => t.trim()).filter(t => t);
                parameters.negative_image_prompts = generationForm.negative_image_prompts.split(',').map(t => t.trim()).filter(t => t);
                parameters.image_generation_required = false;
                parameters.page_generation_mode = 'custom';
                parameters.blog_generation_mode = 'custom';
                parameters.requested_pages = [];
                parameters.requested_blog_topics = [];
            } else if (operationType === 'generate_blogs') {
                // Blog generation
                parameters.requested_blog_topics = generationForm.requested_blog_topics.split(',').map(t => t.trim()).filter(t => t);
                parameters.blog_generation_mode = 'custom';
                parameters.image_generation_required = generationForm.image_generation_required;
                parameters.page_generation_mode = 'custom';
                parameters.requested_pages = [];

                // Add blog-specific parameters
                if (generationForm.key_value_propositions) {
                    parameters.key_value_propositions = generationForm.key_value_propositions.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.brand_attributes) {
                    parameters.brand_attributes = generationForm.brand_attributes.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.seo_primary_keywords) {
                    parameters.seo_primary_keywords = generationForm.seo_primary_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.seo_secondary_keywords) {
                    parameters.seo_secondary_keywords = generationForm.seo_secondary_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                parameters.text_voice = generationForm.text_voice;
                parameters.text_formality = generationForm.text_formality;
                if (generationForm.text_emotion_keywords) {
                    parameters.text_emotion_keywords = generationForm.text_emotion_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.content_to_exclude) {
                    parameters.content_to_exclude = generationForm.content_to_exclude.split(',').map(t => t.trim()).filter(t => t);
                }

                if (generationForm.image_generation_required) {
                    parameters.requested_image_prompts = generationForm.requested_image_prompts.split(',').map(t => t.trim()).filter(t => t);

                    // Process structured image prompts if provided
                    if (generationForm.structured_image_prompts) {
                        const structuredPrompts = generationForm.structured_image_prompts
                            .split('\n')
                            .map(line => line.trim())
                            .filter(line => line && line.includes(':'))
                            .map(line => {
                                const [identifier, prompt] = line.split(':').map(s => s.trim());
                                return { identifier, prompt };
                            });
                        if (structuredPrompts.length > 0) {
                            parameters.requested_image_prompts = structuredPrompts;
                        }
                    }

                    parameters.image_art_style = generationForm.image_art_style;
                    parameters.image_composition_preference = generationForm.image_composition_preference;
                    parameters.image_dominant_colors = generationForm.image_dominant_colors.split(',').map(t => t.trim()).filter(t => t);
                    parameters.negative_image_prompts = generationForm.negative_image_prompts.split(',').map(t => t.trim()).filter(t => t);
                }
            } else if (operationType === 'generate_pages') {
                // Page generation
                parameters.requested_pages = generationForm.requested_pages.split(',').map(t => t.trim()).filter(t => t);
                parameters.page_generation_mode = 'custom';
                parameters.image_generation_required = generationForm.image_generation_required;
                parameters.blog_generation_mode = 'custom';
                parameters.requested_blog_topics = [];

                // Add page-specific parameters
                if (generationForm.key_value_propositions) {
                    parameters.key_value_propositions = generationForm.key_value_propositions.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.brand_attributes) {
                    parameters.brand_attributes = generationForm.brand_attributes.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.seo_primary_keywords) {
                    parameters.seo_primary_keywords = generationForm.seo_primary_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.seo_secondary_keywords) {
                    parameters.seo_secondary_keywords = generationForm.seo_secondary_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                parameters.text_voice = generationForm.text_voice;
                parameters.text_formality = generationForm.text_formality;
                if (generationForm.text_emotion_keywords) {
                    parameters.text_emotion_keywords = generationForm.text_emotion_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.content_to_exclude) {
                    parameters.content_to_exclude = generationForm.content_to_exclude.split(',').map(t => t.trim()).filter(t => t);
                }
                parameters.website_style_preference = generationForm.website_style_preference;
                parameters.primary_call_to_action = generationForm.primary_call_to_action;
                if (generationForm.secondary_call_to_actions) {
                    parameters.secondary_call_to_actions = generationForm.secondary_call_to_actions.split(',').map(t => t.trim()).filter(t => t);
                }

                if (generationForm.image_generation_required) {
                    parameters.requested_image_prompts = generationForm.requested_image_prompts.split(',').map(t => t.trim()).filter(t => t);

                    // Process structured image prompts if provided
                    if (generationForm.structured_image_prompts) {
                        const structuredPrompts = generationForm.structured_image_prompts
                            .split('\n')
                            .map(line => line.trim())
                            .filter(line => line && line.includes(':'))
                            .map(line => {
                                const [identifier, prompt] = line.split(':').map(s => s.trim());
                                return { identifier, prompt };
                            });
                        if (structuredPrompts.length > 0) {
                            parameters.requested_image_prompts = structuredPrompts;
                        }
                    }

                    parameters.image_art_style = generationForm.image_art_style;
                    parameters.image_composition_preference = generationForm.image_composition_preference;
                    parameters.image_dominant_colors = generationForm.image_dominant_colors.split(',').map(t => t.trim()).filter(t => t);
                    parameters.negative_image_prompts = generationForm.negative_image_prompts.split(',').map(t => t.trim()).filter(t => t);
                }
            } else {
                // Complete website
                parameters.image_generation_required = generationForm.image_generation_required;
                parameters.use_default_placeholder_images = generationForm.use_default_placeholder_images;
                parameters.page_generation_mode = generationForm.include_default_pages ? 'standard' : 'custom';
                parameters.blog_generation_mode = generationForm.generate_blogs ? 'standard' : 'custom';

                if (!generationForm.include_default_pages) {
                    // Custom page mode - use only the requested pages
                    parameters.requested_pages = generationForm.requested_pages.split(',').map(t => t.trim()).filter(t => t);
                } else {
                    // Standard mode - requested_pages are additional pages beyond defaults
                    parameters.requested_pages = generationForm.requested_pages ? generationForm.requested_pages.split(',').map(t => t.trim()).filter(t => t) : [];
                }

                if (!generationForm.generate_blogs) {
                    // Custom blog mode - use only the requested topics
                    parameters.requested_blog_topics = generationForm.requested_blog_topics.split('\n').map(t => t.trim()).filter(t => t);
                } else {
                    // Standard mode - requested_blog_topics are additional beyond the 3 minimum
                    parameters.requested_blog_topics = generationForm.requested_blog_topics ? generationForm.requested_blog_topics.split('\n').map(t => t.trim()).filter(t => t) : [];
                }

                if (generationForm.image_generation_required && !generationForm.use_default_placeholder_images) {
                    parameters.requested_image_prompts = generationForm.requested_image_prompts.split(',').map(t => t.trim()).filter(t => t);

                    // Process structured image prompts if provided
                    if (generationForm.structured_image_prompts) {
                        const structuredPrompts = generationForm.structured_image_prompts
                            .split('\n')
                            .map(line => line.trim())
                            .filter(line => line && line.includes(':'))
                            .map(line => {
                                const [identifier, prompt] = line.split(':').map(s => s.trim());
                                return { identifier, prompt };
                            });
                        if (structuredPrompts.length > 0) {
                            parameters.requested_image_prompts = structuredPrompts;
                        }
                    }

                    parameters.image_art_style = generationForm.image_art_style;
                    parameters.image_composition_preference = generationForm.image_composition_preference;
                    parameters.image_dominant_colors = generationForm.image_dominant_colors.split(',').map(t => t.trim()).filter(t => t);
                    parameters.negative_image_prompts = generationForm.negative_image_prompts.split(',').map(t => t.trim()).filter(t => t);
                }

                // Add advanced website parameters
                if (generationForm.key_value_propositions) {
                    parameters.key_value_propositions = generationForm.key_value_propositions.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.brand_attributes) {
                    parameters.brand_attributes = generationForm.brand_attributes.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.seo_primary_keywords) {
                    parameters.seo_primary_keywords = generationForm.seo_primary_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.seo_secondary_keywords) {
                    parameters.seo_secondary_keywords = generationForm.seo_secondary_keywords.split(',').map(t => t.trim()).filter(t => t);
                }
                parameters.text_voice = generationForm.text_voice;
                parameters.text_formality = generationForm.text_formality;
                parameters.website_style_preference = generationForm.website_style_preference;
                parameters.primary_call_to_action = generationForm.primary_call_to_action;
                if (generationForm.secondary_call_to_actions) {
                    parameters.secondary_call_to_actions = generationForm.secondary_call_to_actions.split(',').map(t => t.trim()).filter(t => t);
                }
                if (generationForm.content_to_exclude) {
                    parameters.content_to_exclude = generationForm.content_to_exclude.split(',').map(t => t.trim()).filter(t => t);
                }
            }

            const response = await axiosWithAuth.post(
                `/external/operations/${operationType}`,
                parameters
            );

            setTaskId(response.data.task_id);
            setSuccess(`Content generation started! Task ID: ${response.data.task_id}`);

            // Start polling for task status
            if (response.data.task_id) {
                pollTaskStatus(response.data.task_id);
            }
        } catch (error) {
            setError('Failed to generate content: ' + (error.response?.data?.detail || error.message));
        } finally {
            setGenerating(false);
        }
    };

    const pollTaskStatus = async (taskId) => {
        try {
            const response = await axiosWithAuth.get(`/external/task-status/${taskId}`);
            setTaskStatus(response.data);

            // Continue polling if task is still running
            if (response.data.status === 'RUNNING' || response.data.status === 'QUEUED' || response.data.status === 'PENDING' || response.data.status === 'PROGRESS') {
                setTimeout(() => pollTaskStatus(taskId), 5000); // Poll every 5 seconds
            } else {
                // Task completed (SUCCESS, FAILURE, or other terminal states)
                console.log(`Task ${taskId} completed with status: ${response.data.status}`);
            }
        } catch (error) {
            console.error('Failed to get task status:', error);
        }
    };

    const renderSubscriptionSelector = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                    Subscription & Business Profile
                </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel>Select Subscription</InputLabel>
                    <Select
                        value={selectedSubscription?.id || ''}
                        onChange={(e) => {
                            const subscription = userSubscriptions.find(sub => sub.id === e.target.value);
                            setSelectedSubscription(subscription);
                            if (subscription) {
                                const profile = subscriptionProfiles[subscription.id];
                                if (profile) {
                                    setBusinessProfile(profile);
                                    updateGenerationForm(profile);
                                } else {
                                    setBusinessProfile(null);
                                    setGenerationForm({
                                        entity_name: '',
                                        business_description: '',
                                        services_products_description: '',
                                        target_audience: '',
                                        contact_info: { email: '', phone: '', address: '' },
                                        key_value_propositions: '',
                                        brand_attributes: '',
                                        seo_primary_keywords: '',
                                        seo_secondary_keywords: '',
                                        text_voice: 'Third Person (They/The Company)',
                                        text_formality: 'Formal',
                                        image_art_style: 'photorealistic',
                                        image_composition_preference: '',
                                        image_dominant_colors: '',
                                        negative_image_prompts: '',
                                        website_style_preference: '',
                                        primary_call_to_action: '',
                                        secondary_call_to_actions: '',
                                        content_to_exclude: '',
                                        requested_pages: '',
                                        requested_blog_topics: '',
                                        requested_image_prompts: '',
                                        image_generation_required: true,
                                        use_default_placeholder_images: false,
                                        page_generation_mode: 'standard',
                                        blog_generation_mode: 'standard',
                                        include_default_pages: true,
                                        generate_blogs: true
                                    });
                                }
                            }
                        }}
                        label="Select Subscription"
                    >
                        {userSubscriptions.map((subscription) => (
                            <MenuItem key={subscription.id} value={subscription.id}>
                                {subscription.subscription_name || subscription.plan?.name || 'Unnamed Subscription'} - {subscription.status}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        Business Profile:
                    </Typography>
                    {businessProfile ? (
                        <Chip
                            icon={<Business />}
                            label={businessProfile.entity_name}
                            color="success"
                            size="small"
                        />
                    ) : (
                        <Chip
                            icon={<Warning />}
                            label="Not Available"
                            color="warning"
                            size="small"
                        />
                    )}
                </Box>
            </Grid>
        </Grid>
    );

    const renderBasicFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Business/Entity Name"
                    value={generationForm.entity_name}
                    onChange={(e) => setGenerationForm({ ...generationForm, entity_name: e.target.value })}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Business Description"
                    multiline
                    rows={3}
                    value={generationForm.business_description}
                    onChange={(e) => setGenerationForm({ ...generationForm, business_description: e.target.value })}
                    required
                    helperText="Describe what your business does"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Services/Products Description"
                    multiline
                    rows={3}
                    value={generationForm.services_products_description}
                    onChange={(e) => setGenerationForm({ ...generationForm, services_products_description: e.target.value })}
                    required
                    helperText="Describe your specific services or products"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Target Audience"
                    value={generationForm.target_audience}
                    onChange={(e) => setGenerationForm({ ...generationForm, target_audience: e.target.value })}
                    helperText="Who is your target audience?"
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Contact Email"
                    value={generationForm.contact_info.email}
                    onChange={(e) => setGenerationForm({
                        ...generationForm,
                        contact_info: { ...generationForm.contact_info, email: e.target.value }
                    })}
                    required
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Contact Phone"
                    value={generationForm.contact_info.phone}
                    onChange={(e) => setGenerationForm({
                        ...generationForm,
                        contact_info: { ...generationForm.contact_info, phone: e.target.value }
                    })}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Contact Address"
                    value={generationForm.contact_info.address}
                    onChange={(e) => setGenerationForm({
                        ...generationForm,
                        contact_info: { ...generationForm.contact_info, address: e.target.value }
                    })}
                />
            </Grid>
        </Grid>
    );

    const renderImageFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Image Prompts (identifier:prompt format)"
                    value={generationForm.requested_image_prompts}
                    onChange={(e) => setGenerationForm({ ...generationForm, requested_image_prompts: e.target.value })}
                    required
                    placeholder="hero_banner: Modern tech office with diverse team&#10;about_team: Professional development team collaborating&#10;services_icon: Abstract representation of digital services"
                    helperText="Format: identifier: prompt (one per line). Example: hero_banner: Modern tech office with diverse team"
                />
            </Grid>
        </Grid>
    );

    const renderBlogFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Blog Topics (comma-separated)"
                    value={generationForm.requested_blog_topics}
                    onChange={(e) => setGenerationForm({ ...generationForm, requested_blog_topics: e.target.value })}
                    required
                    helperText="Enter blog topics separated by commas"
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.image_generation_required}
                            onChange={(e) => setGenerationForm({ ...generationForm, image_generation_required: e.target.checked })}
                        />
                    }
                    label="Generate images for blog posts"
                />
            </Grid>
            {/* Image prompts are automatically generated based on blog content - no manual input needed */}
        </Grid>
    );

    const renderPageFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Page Names (comma-separated)"
                    value={generationForm.requested_pages}
                    onChange={(e) => setGenerationForm({ ...generationForm, requested_pages: e.target.value })}
                    required
                    helperText="Enter page names separated by commas (e.g., About, Services, Contact)"
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.image_generation_required}
                            onChange={(e) => setGenerationForm({ ...generationForm, image_generation_required: e.target.checked })}
                        />
                    }
                    label="Generate images for pages"
                />
            </Grid>
            {/* Image prompts are automatically generated based on page content - no manual input needed */}
        </Grid>
    );

    const renderWebsiteFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                    Website Configuration
                </Typography>
            </Grid>

            {/* Page Generation Options */}
            <Grid item xs={12} md={6}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.include_default_pages}
                            onChange={(e) => setGenerationForm({ ...generationForm, include_default_pages: e.target.checked })}
                        />
                    }
                    label="Include default pages (Home, About, Services, Contact)"
                />
            </Grid>

            {!generationForm.include_default_pages && (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Custom Pages (comma-separated)"
                        value={generationForm.requested_pages}
                        onChange={(e) => setGenerationForm({ ...generationForm, requested_pages: e.target.value })}
                        helperText="Enter custom page names separated by commas"
                    />
                </Grid>
            )}

            {/* Additional Pages Field */}
            {generationForm.include_default_pages && (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Additional Pages (comma-separated)"
                        value={generationForm.requested_pages}
                        onChange={(e) => setGenerationForm({ ...generationForm, requested_pages: e.target.value })}
                        helperText="Add pages beyond the default ones (Home, About, Services, Contact)"
                    />
                </Grid>
            )}

            {/* Blog Generation Options */}
            <Grid item xs={12} md={6}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.generate_blogs}
                            onChange={(e) => setGenerationForm({ ...generationForm, generate_blogs: e.target.checked })}
                        />
                    }
                    label="Generate blog posts"
                />
            </Grid>

            {generationForm.generate_blogs && (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Blog Topics (comma-separated, leave empty for default topics)"
                        value={generationForm.requested_blog_topics}
                        onChange={(e) => setGenerationForm({ ...generationForm, requested_blog_topics: e.target.value })}
                        helperText="Enter specific blog topics or leave empty for auto-generated topics"
                    />
                </Grid>
            )}

            {/* Additional Blog Topics Field */}
            {generationForm.generate_blogs && (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Additional Blog Topics (comma-separated)"
                        value={generationForm.additional_blog_topics || ''}
                        onChange={(e) => setGenerationForm({ ...generationForm, additional_blog_topics: e.target.value })}
                        helperText="Add blog topics beyond the default ones"
                    />
                </Grid>
            )}

            {/* Image Generation Options */}
            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                    Image Generation
                </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.image_generation_required}
                            onChange={(e) => setGenerationForm({ ...generationForm, image_generation_required: e.target.checked })}
                        />
                    }
                    label="Generate AI images"
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.use_default_placeholder_images}
                            onChange={(e) => setGenerationForm({ ...generationForm, use_default_placeholder_images: e.target.checked })}
                            disabled={!generationForm.image_generation_required}
                        />
                    }
                    label="Use placeholder images (faster)"
                />
            </Grid>

            {generationForm.image_generation_required && !generationForm.use_default_placeholder_images && (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Additional Image Prompts (identifier:prompt format)"
                        value={generationForm.requested_image_prompts}
                        onChange={(e) => setGenerationForm({ ...generationForm, requested_image_prompts: e.target.value })}
                        placeholder="hero_banner: Modern office environment&#10;team_photo: Diverse professional team collaborating"
                        helperText="Format: identifier: prompt (one per line)"
                    />
                </Grid>
            )}
        </Grid>
    );

    const renderAdvancedFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Key Value Propositions (comma-separated)"
                    value={generationForm.key_value_propositions}
                    onChange={(e) => setGenerationForm({ ...generationForm, key_value_propositions: e.target.value })}
                    helperText="What makes your business unique"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Brand Attributes (comma-separated)"
                    value={generationForm.brand_attributes}
                    onChange={(e) => setGenerationForm({ ...generationForm, brand_attributes: e.target.value })}
                    helperText="How you want your brand to be perceived"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Primary SEO Keywords (comma-separated)"
                    value={generationForm.seo_primary_keywords}
                    onChange={(e) => setGenerationForm({ ...generationForm, seo_primary_keywords: e.target.value })}
                    helperText="Main keywords for SEO"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Secondary SEO Keywords (comma-separated)"
                    value={generationForm.seo_secondary_keywords}
                    onChange={(e) => setGenerationForm({ ...generationForm, seo_secondary_keywords: e.target.value })}
                    helperText="Additional keywords for SEO"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Website Style Preference"
                    value={generationForm.website_style_preference}
                    onChange={(e) => setGenerationForm({ ...generationForm, website_style_preference: e.target.value })}
                    helperText="e.g., 'Modern, clean, and professional'"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Primary Call to Action"
                    value={generationForm.primary_call_to_action}
                    onChange={(e) => setGenerationForm({ ...generationForm, primary_call_to_action: e.target.value })}
                    helperText="e.g., 'Get Free Consultation'"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Secondary Call to Actions (comma-separated)"
                    value={generationForm.secondary_call_to_actions}
                    onChange={(e) => setGenerationForm({ ...generationForm, secondary_call_to_actions: e.target.value })}
                    helperText="e.g., 'View Portfolio, Read Case Studies'"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Content to Exclude (comma-separated)"
                    value={generationForm.content_to_exclude}
                    onChange={(e) => setGenerationForm({ ...generationForm, content_to_exclude: e.target.value })}
                    helperText="Topics or phrases to avoid"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel>Text Voice</InputLabel>
                    <Select
                        value={generationForm.text_voice}
                        onChange={(e) => setGenerationForm({ ...generationForm, text_voice: e.target.value })}
                        label="Text Voice"
                    >
                        <MenuItem value="First Person (I/We)">First Person (I/We)</MenuItem>
                        <MenuItem value="Second Person (You)">Second Person (You)</MenuItem>
                        <MenuItem value="Third Person (They/The Company)">Third Person (They/The Company)</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel>Text Formality</InputLabel>
                    <Select
                        value={generationForm.text_formality}
                        onChange={(e) => setGenerationForm({ ...generationForm, text_formality: e.target.value })}
                        label="Text Formality"
                    >
                        <MenuItem value="Very Formal">Very Formal</MenuItem>
                        <MenuItem value="Formal">Formal</MenuItem>
                        <MenuItem value="Neutral">Neutral</MenuItem>
                        <MenuItem value="Informal">Informal</MenuItem>
                        <MenuItem value="Very Informal/Conversational">Very Informal/Conversational</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel>Image Art Style</InputLabel>
                    <Select
                        value={generationForm.image_art_style}
                        onChange={(e) => setGenerationForm({ ...generationForm, image_art_style: e.target.value })}
                        label="Image Art Style"
                    >
                        <MenuItem value="photorealistic">Photorealistic</MenuItem>
                        <MenuItem value="illustration_flat">Flat Illustration</MenuItem>
                        <MenuItem value="illustration_3d_cartoon">3D Cartoon</MenuItem>
                        <MenuItem value="abstract_geometric">Abstract Geometric</MenuItem>
                        <MenuItem value="watercolor">Watercolor</MenuItem>
                        <MenuItem value="pixel_art">Pixel Art</MenuItem>
                        <MenuItem value="dark_moody">Dark Moody</MenuItem>
                        <MenuItem value="light_airy">Light Airy</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Image Composition Preference"
                    value={generationForm.image_composition_preference}
                    onChange={(e) => setGenerationForm({ ...generationForm, image_composition_preference: e.target.value })}
                    helperText="e.g., 'dynamic action shots with diverse people'"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Dominant Colors (comma-separated)"
                    value={generationForm.image_dominant_colors}
                    onChange={(e) => setGenerationForm({ ...generationForm, image_dominant_colors: e.target.value })}
                    helperText="e.g., '#2563EB', 'blue', 'white'"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Negative Image Prompts (comma-separated)"
                    value={generationForm.negative_image_prompts}
                    onChange={(e) => setGenerationForm({ ...generationForm, negative_image_prompts: e.target.value })}
                    helperText="What to avoid in images"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={generationForm.use_default_placeholder_images}
                            onChange={(e) => setGenerationForm({ ...generationForm, use_default_placeholder_images: e.target.checked })}
                        />
                    }
                    label="Use Default Placeholder Images"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Custom Banner Placeholder URL"
                    value={generationForm.default_banner_placeholder_url}
                    onChange={(e) => setGenerationForm({ ...generationForm, default_banner_placeholder_url: e.target.value })}
                    helperText="URL for custom banner placeholder"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Custom Content Placeholder URL"
                    value={generationForm.default_content_placeholder_url}
                    onChange={(e) => setGenerationForm({ ...generationForm, default_content_placeholder_url: e.target.value })}
                    helperText="URL for custom content placeholder"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Homepage Section Order (comma-separated)"
                    value={generationForm.homepage_section_order_preference}
                    onChange={(e) => setGenerationForm({ ...generationForm, homepage_section_order_preference: e.target.value })}
                    helperText="e.g., 'Hero, About, Services, CTA'"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Logo URL"
                    value={generationForm.logo_url}
                    onChange={(e) => setGenerationForm({ ...generationForm, logo_url: e.target.value })}
                    helperText="URL to your existing logo"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Social Media Links (comma-separated)"
                    value={generationForm.social_media_links}
                    onChange={(e) => setGenerationForm({ ...generationForm, social_media_links: e.target.value })}
                    helperText="e.g., 'https://linkedin.com/company/example'"
                />
            </Grid>
        </Grid>
    );

    const renderGenerationForm = () => {
        return (
            <Box>
                {renderSubscriptionSelector()}
                <Divider sx={{ my: 2 }} />
                {renderBasicFields()}

                {generationType === 'images' && renderImageFields()}
                {generationType === 'blogs' && renderBlogFields()}
                {generationType === 'pages' && renderPageFields()}
                {generationType === 'website' && renderWebsiteFields()}

                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAdvancedOptions}
                                onChange={(e) => setShowAdvancedOptions(e.target.checked)}
                            />
                        }
                        label="Show Advanced Options"
                    />
                </Box>

                {showAdvancedOptions && (
                    <Accordion sx={{ mt: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Advanced Options</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderAdvancedFields()}
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
        );
    };

    const renderTaskStatus = () => {
        if (!taskStatus) return null;

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Generation Status
                </Typography>
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Chip
                                label={taskStatus.status}
                                color={
                                    taskStatus.status === 'COMPLETED' || taskStatus.status === 'SUCCESS' ? 'success' :
                                        taskStatus.status === 'FAILED' || taskStatus.status === 'FAILURE' ? 'error' :
                                            'warning'
                                }
                                sx={{ mr: 2 }}
                            />
                            <Typography variant="body2">
                                Task ID: {taskId}
                            </Typography>
                        </Box>

                        {(taskStatus.status === 'RUNNING' || taskStatus.status === 'QUEUED' || taskStatus.status === 'PENDING' || taskStatus.status === 'PROGRESS') && (
                            <LinearProgress sx={{ mb: 2 }} />
                        )}

                        {taskStatus.message && (
                            <Typography variant="body2" color="textSecondary">
                                {taskStatus.message}
                            </Typography>
                        )}

                        {taskStatus.result_url && (
                            <Button
                                variant="contained"
                                href={taskStatus.result_url}
                                target="_blank"
                                sx={{ mt: 1, mr: 1 }}
                            >
                                View Results
                            </Button>
                        )}

                        {(taskStatus.status === 'SUCCESS' || taskStatus.status === 'COMPLETED') && taskId && (
                            <Button
                                variant="outlined"
                                onClick={() => window.open(`/external/content/${taskId}/download`, '_blank')}
                                sx={{ mt: 1 }}
                                startIcon={<DownloadIcon />}
                            >
                                Download Content
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Content Generation
            </Typography>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Generation Options */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            border: generationType === 'website' ? 2 : 1,
                            borderColor: generationType === 'website' ? 'primary.main' : 'divider'
                        }}
                        onClick={() => setGenerationType('website')}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <WebIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Complete Website</Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Full website with pages, blogs, and images
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            border: generationType === 'pages' ? 2 : 1,
                            borderColor: generationType === 'pages' ? 'primary.main' : 'divider'
                        }}
                        onClick={() => setGenerationType('pages')}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <CreateIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Specific Pages</Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Generate specific pages with optional images
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            border: generationType === 'blogs' ? 2 : 1,
                            borderColor: generationType === 'blogs' ? 'primary.main' : 'divider'
                        }}
                        onClick={() => setGenerationType('blogs')}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <ArticleIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Blog Posts</Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Generate blog posts with optional images
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            border: generationType === 'images' ? 2 : 1,
                            borderColor: generationType === 'images' ? 'primary.main' : 'divider'
                        }}
                        onClick={() => setGenerationType('images')}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <ImageIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Images Only</Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Generate standalone images only
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Generation Form */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 2,
                    gap: { xs: 2, sm: 0 }
                }}>
                    <Typography variant="h6" gutterBottom>
                        {generationType === 'website' ? 'Complete Website Generation' :
                            generationType === 'pages' ? 'Specific Pages Generation' :
                                generationType === 'blogs' ? 'Blog Posts Generation' : 'Image Generation'}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        {selectedSubscription && (
                            <Chip
                                label={`Using: ${selectedSubscription.subscription_name || selectedSubscription.plan?.name || 'Unnamed Subscription'} Profile`}
                                color="primary"
                                size="small"
                                sx={{ wordBreak: 'break-word' }}
                            />
                        )}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                if (selectedSubscription) {
                                    window.location.href = `/business-profile?subscriptionId=${selectedSubscription.id}`;
                                } else {
                                    window.location.href = '/subscriptions';
                                }
                            }}
                            startIcon={<SettingsIcon />}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Manage Business Profile
                        </Button>
                    </Box>
                </Box>

                {renderGenerationForm()}

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={generating ? <CircularProgress size={20} /> : <ExecuteIcon />}
                        onClick={handleGenerateContent}
                        disabled={generating || !selectedSubscription}
                        fullWidth
                    >
                        {generating ? 'Generating...' : 'Generate Content'}
                    </Button>
                </Box>

                {renderTaskStatus()}
            </Paper>
        </Box>
    );
};

export default ContentGenerator; 